import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Building2, Mail, Phone } from 'lucide-react';
import { crmContactsApi } from '../../api/crm';
import { useCompany } from '../../contexts/CompanyContext';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import type { CrmContactStatus } from '../../types';

// Domain status taxonomy → token-colored text label (never color-only).
const STATUS_COLOR: Record<CrmContactStatus, string> = {
  lead:     'var(--scpa-text-muted)',
  prospect: 'var(--scpa-primary)',
  customer: 'var(--scpa-status-ok)',
  churned:  'var(--scpa-status-error)',
  archived: 'var(--scpa-text-muted)',
};

function ContactStatusLabel({ status }: { status: CrmContactStatus }) {
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color: STATUS_COLOR[status], textTransform: 'capitalize' }}>
      {status}
    </span>
  );
}

export default function ContactList() {
  const { currentCompany } = useCompany();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['crm-contacts', cid, search, status, page],
    queryFn: () => crmContactsApi.list({ search, status, page, limit: 50 }),
    enabled: !!cid,
  });

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="Contacts"
        intro="People you do business with. Click a row to view a contact's details, tickets and deals."
        actions={
          <Link to="/crm/contacts/new" className="btn btn-primary flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> New Contact
          </Link>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-52">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            className="input pl-9 w-full"
            placeholder="Search contacts…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <select className="select w-40" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          <option value="lead">Lead</option>
          <option value="prospect">Prospect</option>
          <option value="customer">Customer</option>
          <option value="churned">Churned</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        {isLoading ? (
          <div className="py-12 text-center text-muted">Loading contacts…</div>
        ) : !data?.contacts.length ? (
          <EmptyState>
            No contacts found. Use <b>New Contact</b> to add one.
          </EmptyState>
        ) : (
          <table className="scpa-table">
            <thead>
              <tr>
                <th className="scpa-th">Name</th>
                <th className="scpa-th">Organization</th>
                <th className="scpa-th">Email</th>
                <th className="scpa-th">Phone</th>
                <th className="scpa-th">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.contacts.map(contact => (
                <tr key={contact.id} className="scpa-row">
                  <td className="scpa-td">
                    <Link to={`/crm/contacts/${contact.id}`} className="flex items-center gap-2.5 group">
                      <div className="w-7 h-7 rounded-full bg-primary-muted flex items-center justify-center text-primary text-xs font-semibold flex-shrink-0">
                        {contact.firstName[0]}{contact.lastName?.[0] ?? ''}
                      </div>
                      <span className="font-medium text-fg group-hover:text-primary">
                        {contact.firstName} {contact.lastName}
                      </span>
                      {contact.jobTitle && <span className="text-muted text-xs">· {contact.jobTitle}</span>}
                    </Link>
                  </td>
                  <td className="scpa-td text-muted">
                    {contact.organization ? (
                      <Link to={`/crm/organizations/${contact.organization.id}`} className="flex items-center gap-1 hover:text-primary">
                        <Building2 className="w-3.5 h-3.5" />
                        {contact.organization.name}
                      </Link>
                    ) : '—'}
                  </td>
                  <td className="scpa-td text-muted">
                    {contact.email ? (
                      <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-primary">
                        <Mail className="w-3.5 h-3.5" />{contact.email}
                      </a>
                    ) : '—'}
                  </td>
                  <td className="scpa-td text-muted">
                    {contact.phone ? (
                      <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{contact.phone}</span>
                    ) : '—'}
                  </td>
                  <td className="scpa-td">
                    <ContactStatusLabel status={contact.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {data && data.total > 50 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-line">
            <span className="text-xs text-muted">
              {(page - 1) * 50 + 1}–{Math.min(page * 50, data.total)} of {data.total}
            </span>
            <div className="flex gap-2">
              <button className="btn btn-secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              <button className="btn btn-secondary" disabled={page * 50 >= data.total} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
